import { reactive } from "vue";
import { useToast } from "primevue/usetoast";
import { TOAST_SAVE_LIFE } from "@/utils/constants";


export function usePageState() {
    const toast = useToast();
    const state = reactive({
        loading: false,
        loadingMessage: 'Loading...',
        error: false,
        errorMessage: '',
        success: false,
    });
    
    const resetState = () => {
        state.loading = false;
        state.error = null;
        state.errorMessage = '';
        state.success = false;
    };

    const setLoadingState = (message = 'Loading...') => {
        state.loading = true;
        state.loadingMessage = message;
        state.error = false;
        state.errorMessage = '';
        state.success = false;
    };

    const hideLoadingState = () => {
      state.loading = false;
    };

    const setErrorState = (error) => {
      let message = error.message;
      state.error = true;
      // Check if the error has a response (i.e., the server responded with a status code)
      if (error.response) {
        const statusCode = error.response.status;
        const errorMessage = error.response.data?.message || message;

        if (statusCode === 404) {
          // Handle 404 Not Found
          message = 'Document not found.';
        } else if (statusCode === 401) {
          // Handle 401 Unauthorized
          message = 'Unauthorized. Please log in again.';
        } else {
          // Handle other status codes
          message = `Error ${statusCode}: ${errorMessage}`;
        }
      } else if (error.request) {
        // The request was made but no response was received
        message = 'No response from server. Please check your network.';
      } 

      state.errorMessage = message;
    };

    const fetch = async (fetchFunction) => {
      setLoadingState();
      try {
        await fetchFunction();
        state.success = true;
      } catch (error) {
        setErrorState(error);
      } finally {
        hideLoadingState();
      }
    };

    const fetchWithMessage = async (fetchFunction, loadingMessage) => {
      setLoadingState(loadingMessage);
      try {
        await fetchFunction();
        state.success = true;
      } catch (error) {
        setErrorState(error);
      } finally {
        hideLoadingState();
      }
    };

    const save = async (saveFunction) => {
      setLoadingState('Saving...');
      try {
        await saveFunction();
        state.success = true;
      } catch (error) {
        setErrorState(error);
      } finally {
        hideLoadingState();
      }
    };

    const saveWithMessage = async (saveFunction, loadingMessage) => {
      setLoadingState(loadingMessage);
      try {
        const result = await saveFunction();
        state.success = true;
        return result;
      } catch (error) {
        setErrorState(error);
      } finally {
        hideLoadingState();
      }
    };

    const focusErrorMessage = (elementName) => {
      scrollIntoView(elementName);
  };
  
  const scrollIntoView = (elementName) => {
    const element = document.getElementById(elementName);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const showToast = (severity, summary, detail) => {
    toast.add({ group: 'top-toast', severity: severity, summary: summary, detail: detail, life: TOAST_SAVE_LIFE });   
  };

  const showSuccessWithSummaryToast = (summary, detail) => {
    showToast('success', summary, detail);
  };

  const showSuccessToast = (detail) => {
    showToast('success', 'Success', detail);
  };

  const setFocus = (elementName) => {
    const element = document.getElementById(elementName);
    if (element) {
      element.focus();
      if (element.select) {
      element.select(); 
      }
    }
  };

  return { state, fetch, fetchWithMessage, save, saveWithMessage, resetState, focusErrorMessage, showToast, showSuccessToast, showSuccessWithSummaryToast, setFocus };

}